import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, CheckCircle2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { EnhancedShareButton } from '../components/EnhancedShareButton';

export default function ActionCard() {
  const [action, setAction] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [followUpAsked, setFollowUpAsked] = useState(false);
  const [impact, setImpact] = useState<'better' | 'same' | 'worse' | null>(null);
  const { accessToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAction();
  }, []);

  const fetchAction = async () => {
    try {
      // TEMPORARY: Generate action from local storage check-ins
      const storedCheckins = localStorage.getItem('tension_checkins');
      const checkins = storedCheckins ? JSON.parse(storedCheckins) : [];
      
      if (checkins.length > 0) {
        const latest = checkins[checkins.length - 1];
        const score = latest.rtiScore || 0;
        const state = score <= 35 ? 'Stable' : score <= 60 ? 'Elastic' : 'High Tension';
        
        let actionData;
        
        if (state === 'High Tension') {
          actionData = {
            category: 'Restraint',
            text: 'Reduce initiation frequency by 30% for 48 hours',
            duration: '48 hours',
            reason: 'Your tension score (RTI) shows elevated tension signals. High pursuit can increase emotional distance. Strategic restraint creates space for natural reconnection and prevents pursuit-withdrawal spirals. This isn\'t withdrawal—it\'s recalibration.',
            completed: false
          };
        } else if (state === 'Elastic') {
          actionData = {
            category: 'Reconnection',
            text: 'Initiate one meaningful, low-pressure conversation',
            duration: '24 hours',
            reason: 'Your tension score shows minor fluctuations. A single quality interaction can stabilize tension before it escalates. Focus on curiosity, not needs. Ask open questions about their world, not your relationship status.',
            completed: false
          };
        } else {
          actionData = {
            category: 'Stability',
            text: 'Maintain your current communication rhythm',
            duration: 'Ongoing',
            reason: 'Your tension score shows healthy equilibrium. The best action is continuation. Don\'t fix what isn\'t broken. Stay consistent with initiation patterns, response times, and emotional availability.',
            completed: false
          };
        }
        
        setAction(actionData);
      }
      
      // Original server code (commented out due to deployment issue)
      /*
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/action`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const data = await response.json();
      setAction(data.action);
      */
    } catch (error) {
      console.error('Failed to fetch action:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    try {
      // TEMPORARY: Store completion in local storage
      localStorage.setItem('tension_action_completed', 'true');
      toast.success('Action marked as complete');
      setAction({ ...action, completed: true });
      setFollowUpAsked(true);
      
      // Original server code (commented out due to deployment issue)
      /*
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/action-complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to mark action complete');
      }

      toast.success('Action marked as complete');
      setAction({ ...action, completed: true });
      */
    } catch (error: any) {
      console.error('Mark complete error:', error);
      toast.error(error.message || 'Failed to update action');
    }
  };

  const handleImpact = (value: 'better' | 'same' | 'worse') => {
    setImpact(value);
    try {
      const raw = localStorage.getItem('tension_action_outcomes');
      const outcomes = raw ? JSON.parse(raw) : [];
      const list = Array.isArray(outcomes) ? outcomes : [];
      list.push({
        at: new Date().toISOString(),
        category: action?.category || 'Unknown',
        impact: value,
      });
      localStorage.setItem('tension_action_outcomes', JSON.stringify(list));
      toast.success('Outcome saved');
    } catch {
      toast.error('Could not save outcome');
    }
  };

  const outcomeSummary = (() => {
    try {
      const raw = localStorage.getItem('tension_action_outcomes');
      const outcomes = raw ? JSON.parse(raw) : [];
      const list = Array.isArray(outcomes) ? outcomes : [];
      if (list.length === 0) return null;
      const recent = list.slice(-20);
      const better = recent.filter((o: any) => o.impact === 'better').length;
      const same = recent.filter((o: any) => o.impact === 'same').length;
      const worse = recent.filter((o: any) => o.impact === 'worse').length;
      return { better, same, worse, total: recent.length };
    } catch {
      return null;
    }
  })();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-neutral-600">Loading...</p>
      </div>
    );
  }

  if (!action) {
    return (
      <div className="min-h-screen tension-page-bg">
        <div className="border-b border-slate-200 bg-white/98 px-6 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.08)] backdrop-blur-[6px]">
          <div className="max-w-2xl mx-auto flex items-center gap-4">
            <button
              onClick={() => navigate('/home')}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-neutral-600" />
            </button>
            <h1 className="text-xl font-light">Action Card</h1>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-6 py-12">
          <Card className="p-8 text-center">
            <p className="text-neutral-600">No action available. Complete a check-in first.</p>
          </Card>
        </div>
      </div>
    );
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Restraint':
        return 'bg-blue-500';
      case 'Reconnection':
        return 'bg-green-500';
      case 'Stability':
        return 'bg-neutral-500';
      case 'Recovery':
        return 'bg-purple-500';
      default:
        return 'bg-neutral-500';
    }
  };

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white/98 px-6 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.08)] backdrop-blur-[6px]">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/home')}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <h1 className="text-xl font-light">Today's Action</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12 space-y-6">
        {/* Main Action Card */}
        <Card className="space-y-6 border-violet-500/30 bg-gradient-to-br from-[#11132a] via-[#171734] to-[#220f39] p-8 text-white">
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div className={`px-3 py-1 rounded-full text-xs ${getCategoryColor(action.category)} text-white`}>
                {action.category}
              </div>
              {action.completed && (
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm">Completed</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-light">{action.text}</h2>
              <p className="text-neutral-400 text-sm">Duration: {action.duration}</p>
            </div>
          </div>

          {!action.completed && (
            <Button
              onClick={handleMarkComplete}
              className="w-full bg-white text-neutral-900 hover:bg-neutral-100"
            >
              Mark as Complete
            </Button>
          )}
        </Card>

        {/* Why This? */}
        <Card className="border-violet-200/70 bg-white/92 p-6 dark:border-violet-900/45 dark:bg-neutral-900/80">
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Info className="w-5 h-5 text-neutral-600" />
                <span className="font-medium">Why this action?</span>
              </div>
              <span className="text-neutral-400 text-sm">{isOpen ? 'Hide' : 'Show'}</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <p className="text-neutral-700">{action.reason}</p>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {(followUpAsked || action.completed) && (
          <Card className="border-violet-200/70 bg-white/92 p-6 dark:border-violet-900/45 dark:bg-neutral-900/80">
            <p className="text-sm font-medium text-neutral-900 mb-3">How did this action feel in real life?</p>
            <div className="grid grid-cols-3 gap-2">
              <Button
                type="button"
                variant={impact === 'better' ? 'default' : 'outline'}
                onClick={() => handleImpact('better')}
              >
                Better
              </Button>
              <Button
                type="button"
                variant={impact === 'same' ? 'default' : 'outline'}
                onClick={() => handleImpact('same')}
              >
                Same
              </Button>
              <Button
                type="button"
                variant={impact === 'worse' ? 'default' : 'outline'}
                onClick={() => handleImpact('worse')}
              >
                Worse
              </Button>
            </div>
            {outcomeSummary && (
              <p className="text-xs text-neutral-600 mt-3">
                Recent outcomes ({outcomeSummary.total}): {outcomeSummary.better} better, {outcomeSummary.same} neutral, {outcomeSummary.worse} worse.
              </p>
            )}
          </Card>
        )}

        {/* Reassurance */}
        <Card className="p-6 bg-neutral-50 border-neutral-200">
          <p className="text-sm text-neutral-700 text-center">
            This guidance is based on your current tension score and behavioral patterns. 
            You're learning restraint—a skill most people never master.
          </p>
        </Card>

        {/* Share Button - Static Embedded */}
        <EnhancedShareButton variant="prominent" />
      </div>
    </div>
  );
}